import {ErrorAction, ErrorCode} from './ErrorsService.constants';
import {DI_TOKENS} from '@/di/di.tokens';

import {injected} from 'brandi';

import {
  HttpError,
  HttpErrorBackend,
  HttpErrorNoResponse,
  HttpErrorProject,
  HttpStatus,
  ProjectUserAuth,
  uuid4,
} from '@specials/project-user';

import {ErrorResourcesBase, ErrorResourcesContent, ResourcesAll} from '@/resources';
import {FrontendError} from '@/services/ErrorsService/FrontendError';
import {LoggerService} from '@/services/LoggerService/LoggerService';
import {NavigationClickerModalService} from '@/services/NavigationClickerModalService/NavigationClickerModalService';
import {NavigationService} from '@/services/NavigationService/NavigationService';
import {PlatformService} from '@/services/PlatformService/PlatformService';
import {ProjectUserInfoData, ProjectUserLoginData} from '@/services/ProjectAuthApi/types';

export interface ErrorBreadcrumbs {
  errorCode: string;
  httpCode?: HttpStatus;
  requestId?: string;
  error?: Error;
  errorMessage?: string;
  timestamp?: string;
}

export interface ErrorContent {
  resources: ErrorResourcesContent;
  breadcrumbs: ErrorBreadcrumbs;
}

export class ErrorsService {
  constructor(
    private readonly resources: ResourcesAll,
    private readonly platformService: PlatformService,
    private readonly navigationService: NavigationService,
    private readonly projectUserAuth: ProjectUserAuth<ProjectUserInfoData, ProjectUserLoginData>,
    private readonly navigationClickerModalService: NavigationClickerModalService,
    private readonly loggerService: LoggerService,
  ) {}

  public buildErrorData = (error: HttpError | unknown): ErrorContent => {
    const breadcrumbs = this.extractBreadcrumbs(error);
    const isApp = this.platformService.isApp();
    const matchedResources = this.extractResources(breadcrumbs);
    const platformResources = matchedResources ?? this.resources.errors.fallback;
    const resources = isApp ? platformResources.mb : platformResources.web;

    // Отдельно логируем в Sentry необработанную ошибку, потому что разработчик
    // забыл добавить для ошибки ресурсы и пользователь стандартный экран
    // "Что-то пошло не так", вместо конкретного описания ошибки
    if (typeof matchedResources === 'undefined') {
      this.loggerService.logUnMatchedError(breadcrumbs);
    }

    return {
      resources,
      breadcrumbs,
    };
  };

  private extractBreadcrumbs = (error: FrontendError | HttpError | unknown): ErrorBreadcrumbs => {
    if (HttpErrorProject.isHttpErrorProject(error)) {
      return {
        httpCode: error.httpCode,
        errorCode: error.errorCode,
        requestId: error.requestId,
        timestamp: error.timestamp,
      };
    }

    if (HttpErrorBackend.isHttpErrorBackend(error)) {
      return {
        httpCode: error.response.httpCode,
        errorCode: error.response.httpCode.toString(),
        requestId: uuid4(),
        timestamp: new Date().toISOString(),
      };
    }

    if (FrontendError.isFrontendError(error)) {
      return {
        errorCode: error.errorCode,
        errorMessage: error.message,
        requestId: error.requestId,
        timestamp: error.timestamp,
        error: error.error,
      };
    }

    // Ошибка axios о разрыве соединения по таймауту
    if (error instanceof HttpErrorNoResponse && error.message.includes('timeout')) {
      return {
        errorCode: ErrorCode.EconnAborted,
        requestId: uuid4(),
        timestamp: new Date().toISOString(),
      };
    }

    return {
      errorCode: ErrorCode.UnmatchedError,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
    };
  };

  private extractResources = (breadcrumbs: ErrorBreadcrumbs): ErrorResourcesBase | undefined => {
    const {errors} = this.resources;

    const {errorCode, httpCode} = breadcrumbs;

    if (errorCode) {
      const resources = errors.errorCodes.find((e) => e.errorCodes.includes(errorCode));
      if (resources) return resources;
    }

    if (httpCode) {
      const resources = errors.httpCodes.find((e) => e.httpCodes.includes(httpCode));
      if (resources) return resources;
    }
  };

  public handleErrorAction = async (action: ErrorAction): Promise<void> => {
    switch (action) {
      case ErrorAction.Logout: {
        void (await this.projectUserAuth.logout());
        break;
      }
      case ErrorAction.Reload: {
        if (this.platformService.isApp()) {
          this.navigationService.pageAppReload();
        } else {
          this.navigationService.pageReload();
        }
        break;
      }
      case ErrorAction.AuthReloadWithoutQueryRef: {
        this.navigationService.authPageReloadWithoutQueryRef();
        break;
      }
      case ErrorAction.LogoutWithReload: {
        void (await this.projectUserAuth.logout());
        this.navigationService.pageReload();
        break;
      }
      case ErrorAction.LogoutWithRedirectToMainForm: {
        void (await this.projectUserAuth.logout());
        this.navigationService.navigateToMainPageForm();
        break;
      }
      case ErrorAction.CloseClickerModal: {
        this.navigationClickerModalService.clearCurrentModalType();
        break;
      }
    }
  };

  // Пропускаем вывод пользователю указанных ошибок
  public isErrorThatCanNotVisibleForUser = (error: HttpError | unknown): boolean => {
    const {errorCode} = this.extractBreadcrumbs(error);

    // ErrorCode.TaskProcessCompleted - возвращается при попытке забрать награду за выполненное задание
    return errorCode === ErrorCode.TaskProcessCompleted;
  };

  // Во время перерасччета лиг большая часть ручек будут возвращать ошибку,
  // на которую нужно показывать пользователю экран перерасчета лиг
  public isErrorRecalculationLeagues = (error: HttpError | unknown): boolean => {
    const {errorCode} = this.extractBreadcrumbs(error);

    return errorCode === ErrorCode.RecalculationLeagues;
  };

  // Пользователь зашел на завершающей стадии проекта
  public isErrorGameIsNotAProgress = (error: HttpError | unknown): boolean => {
    const {errorCode} = this.extractBreadcrumbs(error);

    return errorCode === ErrorCode.GameIsNotAProgress;
  };
}

injected(
  ErrorsService,
  DI_TOKENS.resources,
  DI_TOKENS.platformService,
  DI_TOKENS.navigationService,
  DI_TOKENS.projectUserAuth,
  DI_TOKENS.navigationClickerModalService,
  DI_TOKENS.loggerService,
);
